import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Helper function to convert string boolean values to actual booleans.
 * Handles 'yes'/'no' and 'true'/'false' string values.
 *
 * @param value - String value to convert
 * @returns Boolean value or undefined if not a valid boolean string
 *
 * @example
 * ```typescript
 * convertStringToBoolean('yes')    // returns true
 * convertStringToBoolean('no')     // returns false
 * convertStringToBoolean('true')   // returns true
 * convertStringToBoolean('false')  // returns false
 * convertStringToBoolean('')       // returns undefined
 * convertStringToBoolean(undefined) // returns undefined
 * ```
 */
export function convertStringToBoolean(
  value: string | undefined
): boolean | undefined {
  if (!value) return undefined;
  const lowerValue = value.toLowerCase();
  if (lowerValue === 'true' || lowerValue === 'yes') return true;
  if (lowerValue === 'false' || lowerValue === 'no') return false;
  return undefined;
}

/**
 * Helper function to format Date objects to YYYY-MM-DD format for API submission.
 * Uses ISO 8601 date format standard.
 *
 * @param date - Date object to format
 * @returns Formatted date string or undefined if date is not provided
 *
 * @example
 * ```typescript
 * formatDateForApi(new Date('2025-12-20')) // returns '2025-12-20'
 * formatDateForApi(undefined)              // returns undefined
 * ```
 */
export function formatDateForApi(date: Date | undefined): string | undefined {
  return date?.toISOString().split('T')[0];
}
